import qs from '@bundled-es-modules/qs'
import type { NitroFetchOptions } from 'nitropack'

interface ClientSettings {
  withoutCookies?: boolean
}

export const useClient = <T>(
  baseUrl: string,
  url: string,
  options: NitroFetchOptions<'json'> = {},
  settings: ClientSettings = {},
): Promise<T> => {
  options.baseURL = baseUrl
  options.credentials = 'include'
  options.mode = 'cors'
  options.cache = 'no-cache'
  options.onRequest = ({ options }) => {
    if (options.query) {
      const convertedOptions = new URLSearchParams(qs.stringify(options.query))
      options.query = Object.fromEntries(convertedOptions)
    }
  }
  const { handleException } = useHandleException()

  let headers = Object.assign(
    {
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json, text/plain, */*',
      'Origin': useRequestHeaders(['host']).host,
      'X-Forwarded-Host': useRequestHeaders(['host']).host,
    },
    useRequestHeaders(['referer']) as Record<string, string>,
  )

  if (!settings?.withoutCookies) {
    headers = Object.assign(headers, useRequestHeaders(['cookie']) as Record<string, string>)
  }

  if (options.headers) {
    options.headers = Object.assign({}, headers, options.headers)
  }
  else {
    options.headers = headers
  }

  options.onResponseError = ({ response }): Promise<void> | void => {
    if (response) {
      if ([403].includes(response.status)) {
        throw createError({
          statusCode: response.status,
          statusMessage: response._data.message || 'Unexpected error',
        })
      }
      else if (response.status >= 500) {
        try {
          handleException(response)
        }
        catch (ex: unknown) {
          console.error('Sentry instance is not available', ex)
        }

        throw createError({
          statusCode: response.status,
          statusMessage: response._data.message || 'Unexpected error',
          fatal: true,
        })
      }
    }
  }

  /**
   * Convert all responses to camel case
   * @param responseText
   * @returns
   */
  options.parseResponse = (responseText: string) => {
    return useKeysToCamelCase(JSON.parse(responseText))
  }

  const { token: csrfToken } = useCSRFToken()

  if (csrfToken.value !== '') {
    options.headers = Object.assign(
      {},
      {
        'x-xsrf-token': csrfToken.value,
      },
      options.headers,
    )
  }

  return $fetch(url, <NitroFetchOptions<'json'>>options)
}
